import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { LocationPicker } from "@/components/LocationPicker/LocationPicker";
import { FieldInput } from "@/components/businesses/fields/FieldInput";
import { Label } from "@/components/ui/label";
import type { WizardValues } from "./schema";

const DEFAULT_CENTER = { lat: 9.9312, lng: 76.2673 };

export const LocationStep = () => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WizardValues>();
  const lat = watch("lat");
  const lng = watch("lng");

  const handlePickerChange = useCallback(
    (v: { lat: number; lng: number }) => {
      setValue("lat", v.lat, { shouldValidate: true });
      setValue("lng", v.lng, { shouldValidate: true });
    },
    [setValue],
  );

  const handlePlaceSelected = useCallback(
    (p: {
      lat: number;
      lng: number;
      displayName: string;
      address: string;
      city: string;
      district: string;
      pincode: string;
    }) => {
      setValue("lat", p.lat, { shouldValidate: true });
      setValue("lng", p.lng, { shouldValidate: true });
      if (p.address) setValue("address", p.address, { shouldValidate: true });
      if (p.city) setValue("city", p.city, { shouldValidate: true });
      if (p.district)
        setValue("district", p.district, { shouldValidate: true });
      if (p.pincode) setValue("pincode", p.pincode, { shouldValidate: true });
    },
    [setValue],
  );

  const latLngError = errors.lat?.message || errors.lng?.message;

  return (
    <div className="space-y-4">
      <div>
        <Label>
          Location<span className="text-destructive ml-0.5">*</span>
        </Label>
        <div className="mt-1">
          <LocationPicker
            value={{
              lat: typeof lat === "number" ? lat : DEFAULT_CENTER.lat,
              lng: typeof lng === "number" ? lng : DEFAULT_CENTER.lng,
            }}
            onChange={handlePickerChange}
            onPlaceSelected={handlePlaceSelected}
          />
        </div>
        {latLngError ? (
          <p className="text-xs text-destructive mt-1">
            {latLngError as string}
          </p>
        ) : null}
      </div>

      <FieldInput<WizardValues>
        name="address"
        label="Address"
        required
        textarea
        rows={2}
      />
      <div className="grid md:grid-cols-3 gap-3">
        <FieldInput<WizardValues> name="city" label="City" required />
        <FieldInput<WizardValues> name="district" label="District" />
        <FieldInput<WizardValues>
          name="pincode"
          label="Pincode"
          required
          placeholder="6 digits"
        />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <FieldInput<WizardValues>
          name="phone"
          label="Phone"
          required
          placeholder="+91…"
          help="Format: +91 followed by 10 digits"
        />
        <FieldInput<WizardValues>
          name="whatsapp"
          label="WhatsApp"
          placeholder="+91…"
        />
        <FieldInput<WizardValues> name="email" label="Email" type="email" />
      </div>
      <p className="text-sm font-medium text-muted-foreground mt-2">
        Social media (optional)
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        <FieldInput<WizardValues>
          name="instagramUrl"
          label="Instagram URL"
          placeholder="https://instagram.com/…"
        />
        <FieldInput<WizardValues>
          name="facebookUrl"
          label="Facebook URL"
          placeholder="https://facebook.com/…"
        />
        <FieldInput<WizardValues>
          name="youtubeUrl"
          label="YouTube URL"
          placeholder="https://youtube.com/@…"
        />
      </div>
    </div>
  );
};
